/**
 * Utility functions for training expiration calculations
 */

// Building ID for 3 World Trade Center
export const BUILDING_3_WTC_ID = 'f9a9eee0-655b-472f-9b91-432cddd9b67d';

/**
 * Check if a building ID corresponds to Building 3 World Trade Center
 * @param {string} buildingId - The building ID to check
 * @returns {boolean} - True if it's Building 3 WTC
 */
export const isBuilding3WTC = (buildingId) => {
    return buildingId === BUILDING_3_WTC_ID;
};

/**
 * Check if a user/tenant belongs to Building 3 World Trade Center
 * This function checks the d1 field which contains building information
 * @param {string} d1 - The d1 field containing hierarchical building/tenant info
 * @returns {boolean} - True if the user/tenant belongs to Building 3 WTC
 */
export const isTenantInBuilding3WTC = (d1) => {
    if (!d1) return false;

    // d1 format is typically: clientId#null#propertyId#buildingId or similar
    const parts = d1.split('#');
    // The building ID is typically in the 4th position (index 3)
    const buildingId = parts[3];

    return isBuilding3WTC(buildingId);
};

/**
 * Get the training expiration period in years for a given building
 * @param {string} buildingId - The building ID
 * @returns {number} - Number of years (2 for 3 WTC, 1 for others)
 */
export const getTrainingExpirationYears = (buildingId) => {
    return isBuilding3WTC(buildingId) ? 2 : 1;
};

/**
 * Get the training expiration period in days for renewal checks
 * @param {string} buildingId - The building ID  
 * @returns {number} - Number of days (723 for 3 WTC (2 years - 7 days), 358 for others (1 year - 7 days))
 */
export const getTrainingRenewalThresholdDays = (buildingId) => {
    return isBuilding3WTC(buildingId) ? 723 : 358; // 2*365 - 7 = 723, 365 - 7 = 358
};

/**
 * Check if a building ID or d1 indicates Building 3 WTC (flexible function)
 * @param {string} identifier - Either a building ID or d1 string
 * @returns {boolean} - True if it's Building 3 WTC
 */
export const isFromBuilding3WTC = (identifier) => {
    if (!identifier) return false;

    // If it's a direct building ID match
    if (identifier === BUILDING_3_WTC_ID) return true;

    // If it contains # symbols, treat as d1 format
    if (identifier.includes('#')) {
        return isTenantInBuilding3WTC(identifier);
    }

    // Otherwise, treat as a building ID
    return isBuilding3WTC(identifier);
};
